import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useMemo } from "react"
import { m2mCleaning_cleanerV2 } from "../endpoints/m2m-cleaning.cleaner-v2"
import { m2mCleaning_cleanerV3 } from "@/endpoints/m2m-cleaning.cleaner-v3"
import { uploadBlobWithPresignedPut } from "@/utils/presignedUpload"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"

export const requestDeletePhotoTourImage = async (
    token: string,
    operationId: string,
    imageId: string
): Promise<{ err: boolean }> => {
    const { error } = await bearerFetcher(
        token,
        m2mCleaning_cleanerV4.deletePhotoTourImageByCleaner({
            operationId,
            imageId
        }),
        { method: "DELETE" }
    )
    return { err: !!error }
}

export const requestDeletePhotoTourMovie = async (
    token: string,
    operationId: string,
    movieId: string
): Promise<{ err: boolean }> => {
    const { error } = await bearerFetcher(
        token,
        m2mCleaning_cleanerV4.deletePhotoTourMovieByCleaner({
            operationId,
            movieId
        }),
        { method: "DELETE" }
    )
    return { err: !!error }
}

export interface PhotoTour {
    id: string
    companyId: string
    title: string
    description: string
    createdAt: number
    updatedAt: number
}

export interface PhotoTourItem {
    id: string
    photoTourId: string
    title: string
    description: string
    createdAt: number
    updatedAt: number
    orderKey: number
}

export interface PhotoTourItemSampleImage {
    id: string
    photoTourItemId: string
    description: string
    url: string
    createdAt: number
    updatedAt: number
}

export const usePhotoTourByCleaningId = (
    token: string,
    cleaningId: string | undefined
) => {
    const { data, error } = useAuthBearerFetch<{
        photoTour: PhotoTour
        items: PhotoTourItem[]
        sampleImagesMap: Record<string, PhotoTourItemSampleImage[]>
    }>(
        token,
        cleaningId
            ? m2mCleaning_cleanerV2.findPhotoTourByCleaningId({
                  cleaningId
              })
            : null,
        {
            swrConfig: {
                shouldRetryOnError: false
            }
        }
    )
    return useMemo(() => {
        return {
            data: data
                ? {
                      ...data,
                      items: data.items
                          .slice()
                          .sort((a, b) => (a.orderKey > b.orderKey ? 1 : -1))
                  }
                : undefined,

            error
        }
    }, [data, error])
}

// 即時アップロード用: 選択時に issue + S3 PUT(pending) のみ行い uploadId を返す。
export const requestPutPhotoTourImage = async (
    token: string,
    cleaningId: string,
    photoTourItemId: string,
    body: Blob,
    signal?: AbortSignal
): Promise<{ uploadId: string }> => {
    const issueUrl = m2mCleaning_cleanerV2.issuePhotoTourImageUploadUrl({
        cleaningId,
        photoTourItemId
    })
    return uploadBlobWithPresignedPut({ token, issueUrl, blob: body, signal })
}

// 即時アップロード用: 送信時に complete のみ行う。確定された画像 id を返す。
export const completePhotoTourImage = async (
    token: string,
    cleaningId: string,
    photoTourItemId: string,
    uploadId: string
): Promise<{ data?: { id: string }; error?: unknown }> => {
    try {
        const completeUrl = m2mCleaning_cleanerV2.completePhotoTourImageUpload({
            cleaningId,
            photoTourItemId
        })
        const { data, error } = await bearerFetcher<{ id: string }>(
            token,
            completeUrl,
            { method: "POST", body: JSON.stringify({ uploadId }) }
        )
        if (error) {
            return { data: undefined, error }
        }
        if (!data?.id) {
            return {
                data: undefined,
                error: new Error("id missing in complete response")
            }
        }
        return { data, error: undefined }
    } catch (error) {
        return { data: undefined, error }
    }
}

export const requestUpdateUploadedPhotoTourImage = async (
    token: string,
    imageId: string,
    description: string
) => {
    return bearerFetcher(
        token,
        m2mCleaning_cleanerV3.photoTourImage({
            imageId
        }),
        {
            method: "PUT",
            body: JSON.stringify({ description })
        }
    )
}

export const requestUpdateUploadedPhotoTourMovie = async (
    token: string,
    movieId: string,
    description: string
) => {
    return bearerFetcher(
        token,
        m2mCleaning_cleanerV3.photoTourVideo({
            movieId
        }),
        {
            method: "PUT",
            body: JSON.stringify({ description })
        }
    )
}
